import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useDispatch } from 'react-redux'
import Swal from 'sweetalert2'
import { addSubscriptionPlans } from '../../slice/subscriptionPlanSlice'

// Validation Schema
const subscriptionPlanSchema = yup.object().shape({
    name: yup
        .string()
        .required('Plan name is required')
        .min(3, 'Plan name must be at least 3 characters')
        .max(100, 'Plan name cannot exceed 100 characters'),
    
    description: yup
        .string()
        .required('Description is required')
        .min(10, 'Description must be at least 10 characters')
        .max(500, 'Description cannot exceed 500 characters'),
    
    duration_type: yup
        .string()
        .required('Duration type is required')
        .oneOf(['monthly', 'quarterly', 'yearly', 'lifetime'], 'Please select a valid duration type'),
    
    duration_days: yup
        .number()
        .required('Duration in days is required')
        .min(1, 'Duration must be at least 1 day')
        .max(36500, 'Duration cannot exceed 36500 days (100 years)')
        .typeError('Duration must be a number'),
    
    price: yup
        .number()
        .required('Price is required')
        .min(0, 'Price cannot be negative')
        .typeError('Price must be a number'),
    
    apple_product_id: yup
        .string()
        .required('Apple Product ID is required')
        .min(5, 'Apple Product ID must be at least 5 characters')
        .max(200, 'Apple Product ID cannot exceed 200 characters')
        .matches(/^[a-zA-Z0-9._-]+$/, 'Apple Product ID can only contain letters, numbers, dots, hyphens, and underscores'),
    
    google_product_id: yup
        .string()
        .required('Google Product ID is required')
        .min(5, 'Google Product ID must be at least 5 characters')
        .max(200, 'Google Product ID cannot exceed 200 characters')
        .matches(/^[a-zA-Z0-9._-]+$/, 'Google Product ID can only contain letters, numbers, dots, hyphens, and underscores'),
})

const AddSubscriptionPlan = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
        reset,
        setValue
    } = useForm({
        resolver: yupResolver(subscriptionPlanSchema),
        defaultValues: {
            name: '',
            description: '',
            duration_type: '',
            duration_days: '',
            price: '',
            apple_product_id: '',
            google_product_id: '',
        }
    })

    // Watch duration type for auto-fill duration days
    const durationType = watch('duration_type')
    const price = watch('price')
    const durationDays = watch('duration_days')

    // Auto-fill duration days based on duration type
    const handleDurationTypeChange = (e) => {
        const type = e.target.value
        setValue('duration_type', type)
        
        // Auto-suggest duration days
        const durationMap = {
            monthly: 30,
            quarterly: 90,
            yearly: 365,
            lifetime: 36500
        }
        
        if (durationMap[type]) {
            setValue('duration_days', durationMap[type])
        }
    }

    // Calculate price per day
    const getPricePerDay = () => {
        if (price && durationDays && price > 0 && durationDays > 0) {
            return (parseFloat(price) / parseFloat(durationDays)).toFixed(2)
        }
        return null
    }

    const onSubmit = async (data) => {
        setIsSubmitting(true)

        try {
            // Build payload
            const payload = {
                name: data.name,
                description: data.description,
                duration_type: data.duration_type,
                duration_days: Number(data.duration_days),
                price: Number(data.price),
                apple_product_id: data.apple_product_id,
                google_product_id: data.google_product_id,
            }

            const response = await dispatch(addSubscriptionPlans(payload))

            if (response?.payload?.code === 200) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: response?.payload?.message || 'Subscription plan created successfully',
                    timer: 2000,
                    showConfirmButton: false,
                })

                reset()
                setTimeout(() => navigate('/subscriptions'), 2000)
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response?.payload?.message || 'Failed to create subscription plan',
                })
            }

        } catch (error) {
            console.error(error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.message || 'Something went wrong',
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className="container-fluid px-4 py-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-semibold mb-0">Add Subscription Plan</h4>
                <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/subscriptions')}
                    style={{
                        padding: '8px 20px',
                        fontSize: '14px',
                        borderRadius: '6px'
                    }}
                >
                    ← Back to List
                </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="row">
                    {/* Plan Information */}
                    <div className="col-12 mb-4">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                                <i className="fe-info me-2"></i>
                                Plan Information
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    {/* Plan Name */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Plan Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                            placeholder="e.g., Premium Annual"
                                            {...register('name')}
                                        />
                                        {errors.name && (
                                            <div className="invalid-feedback">{errors.name.message}</div>
                                        )}
                                        <small className="text-muted">
                                            Enter a clear, descriptive name for the plan
                                        </small>
                                    </div>

                                    {/* Duration Type */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Duration Type <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            className={`form-select ${errors.duration_type ? 'is-invalid' : ''}`}
                                            {...register('duration_type')}
                                            onChange={handleDurationTypeChange}
                                        >
                                            <option value="">Select duration type</option>
                                            <option value="monthly">Monthly</option>
                                            <option value="quarterly">Quarterly</option>
                                            <option value="yearly">Yearly</option>
                                            <option value="lifetime">Lifetime</option>
                                        </select>
                                        {errors.duration_type && (
                                            <div className="invalid-feedback">{errors.duration_type.message}</div>
                                        )}
                                        <small className="text-muted">
                                            Select the billing cycle type
                                        </small>
                                    </div>

                                    {/* Description */}
                                    <div className="col-12">
                                        <label className="form-label">
                                            Description <span className="text-danger">*</span>
                                        </label>
                                        <textarea
                                            className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                                            placeholder="Describe the features and benefits of this plan (e.g., Unlimited access to all premium features)"
                                            rows="4"
                                            {...register('description')}
                                        />
                                        {errors.description && (
                                            <div className="invalid-feedback">{errors.description.message}</div>
                                        )}
                                        <small className="text-muted">
                                            Minimum 10 characters, maximum 500 characters
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Duration & Pricing */}
                    <div className="col-12 mb-4">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                                <i className="fe-clock me-2"></i>
                                Duration & Pricing
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    {/* Duration Days */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Duration (Days) <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            className={`form-control ${errors.duration_days ? 'is-invalid' : ''}`}
                                            placeholder="Enter number of days"
                                            {...register('duration_days')}
                                            min="1"
                                            max="36500"
                                        />
                                        {errors.duration_days && (
                                            <div className="invalid-feedback">{errors.duration_days.message}</div>
                                        )}
                                        <small className="text-muted">
                                            {durationType === 'monthly' && 'Suggested: 30 days'}
                                            {durationType === 'quarterly' && 'Suggested: 90 days'}
                                            {durationType === 'yearly' && 'Suggested: 365 days'}
                                            {durationType === 'lifetime' && 'Suggested: 36500 days (100 years)'}
                                            {!durationType && 'Total number of days the plan is valid'}
                                        </small>
                                    </div>

                                    {/* Price */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Price (AED) <span className="text-danger">*</span>
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">AED</span>
                                            <input
                                                type="number"
                                                className={`form-control ${errors.price ? 'is-invalid' : ''}`}
                                                placeholder="0.00"
                                                {...register('price')}
                                                min="0"
                                                step="0.01"
                                            />
                                            {errors.price && (
                                                <div className="invalid-feedback">{errors.price.message}</div>
                                            )}
                                        </div>
                                        <small className="text-muted">
                                            Enter 0 for a free plan
                                        </small>
                                    </div>

                                    {/* Price Per Day Display */}
                                    {getPricePerDay() && (
                                        <div className="col-12">
                                            <div className="alert alert-info mb-0" style={{ borderRadius: '6px' }}>
                                                <i className="fe-info" style={{ fontSize: '16px' }}></i>
                                                <strong className="ms-2">Price per day:</strong> AED {getPricePerDay()}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* App Store Configuration */}
                    <div className="col-12 mb-4">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                                <i className="fe-smartphone me-2"></i>
                                App Store Configuration
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    {/* Apple Product ID */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Apple Product ID <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.apple_product_id ? 'is-invalid' : ''}`}
                                            placeholder="com.myapp.premium.yearly"
                                            {...register('apple_product_id')}
                                            style={{
                                                fontFamily: 'monospace',
                                                fontSize: '13px'
                                            }}
                                        />
                                        {errors.apple_product_id && (
                                            <div className="invalid-feedback">{errors.apple_product_id.message}</div>
                                        )}
                                        <small className="text-muted">
                                            Product ID from Apple App Store Connect
                                        </small>
                                    </div>

                                    {/* Google Product ID */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Google Product ID <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.google_product_id ? 'is-invalid' : ''}`}
                                            placeholder="premium_yearly_012"
                                            {...register('google_product_id')}
                                            style={{
                                                fontFamily: 'monospace',
                                                fontSize: '13px'
                                            }}
                                        />
                                        {errors.google_product_id && (
                                            <div className="invalid-feedback">{errors.google_product_id.message}</div>
                                        )}
                                        <small className="text-muted">
                                            Product ID from Google Play Console
                                        </small>
                                    </div>

                                    {/* Info Alert */}
                                    <div className="col-12">
                                        <div className="alert alert-warning mb-0" style={{ borderRadius: '6px' }}>
                                            <i className="fe-alert-triangle" style={{ fontSize: '16px' }}></i>
                                            <strong className="ms-2">Important:</strong> Make sure these Product IDs match exactly with the ones configured in Apple App Store Connect and Google Play Console for In-App Purchase integration.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Preview Card */}
                    {watch('name') && watch('price') !== '' && durationType && (
                        <div className="col-12 mb-4">
                            <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                                <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                                    <i className="fe-eye me-2"></i>
                                    Preview
                                </div>
                                <div className="card-body p-4">
                                    <div className="text-center">
                                        <div
                                            style={{
                                                display: 'inline-block',
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                padding: '40px 60px',
                                                borderRadius: '16px',
                                                color: 'white',
                                                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                                                minWidth: '350px'
                                            }}
                                        >
                                            <h3
                                                style={{
                                                    fontSize: '32px',
                                                    fontWeight: '700',
                                                    marginBottom: '20px'
                                                }}
                                            >
                                                {watch('name')}
                                            </h3>
                                            
                                            <div className="mb-3">
                                                {watch('price') == 0 ? (
                                                    <div style={{ fontSize: '48px', fontWeight: '700' }}>
                                                        FREE
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <span style={{ fontSize: '24px', opacity: 0.9 }}>AED</span>
                                                        <span style={{ fontSize: '56px', fontWeight: '700', margin: '0 8px' }}>
                                                            {parseFloat(watch('price') || 0).toFixed(0)}
                                                        </span>
                                                        <span style={{ fontSize: '24px', opacity: 0.9 }}>
                                                            / {durationType}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                            {watch('description') && (
                                                <p
                                                    style={{
                                                        fontSize: '16px',
                                                        opacity: 0.95,
                                                        marginBottom: '20px'
                                                    }}
                                                >
                                                    {watch('description')}
                                                </p>
                                            )}

                                            {durationDays && (
                                                <div
                                                    style={{
                                                        fontSize: '14px',
                                                        opacity: 0.9
                                                    }}
                                                >
                                                    <i className="fe-clock"></i> {durationDays} days
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Form Actions */}
                <div className="d-flex gap-3 justify-content-end">
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => navigate('/subscriptions')}
                        disabled={isSubmitting}
                        style={{
                            padding: '10px 24px',
                            fontSize: '14px',
                            borderRadius: '6px'
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn btn-success"
                        disabled={isSubmitting}
                        style={{
                            padding: '10px 24px',
                            fontSize: '14px',
                            borderRadius: '6px',
                            minWidth: '120px'
                        }}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Creating...
                            </>
                        ) : (
                            'Create Plan'
                        )}
                    </button>
                </div>
            </form>
        </div>
    )
}

export default AddSubscriptionPlan;